import React from 'react';
import Icon from '@ant-design/icons/lib/components/Icon';
import { Button } from 'antd';
import moment from 'moment';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import cx from 'classnames';
import { ReactComponent as LockIcon } from '../../../../assets/sightx-icons/svg/sx-lock.svg';
import { BlockedByFeature, BlockedByFeatureType, notAvailablePropExceptIn } from '../../../../featureToggles/features/utils';
import { selectors as accountListSelectors } from '../../../../slices/accounts/list';
import { selectors as authSelectors } from '../../../../slices/auth';
import { RootState } from '../../../../slices/rootReducer';
import { CadenceTypesValues } from '../../../auth/proRegister/subscriptionTypes';
import { AccessTypes } from '../../../common/domain/accountAccessType';
import { SxTooltip } from '../../../common/utils/wrapComponents';
import { CurrentPlan } from '../common/currentPlan';
import { OtherPlans } from '../common/otherPlans';
import { AddSeatsModal } from '../common/addSeatsModal';
import { MAX_ALLOWED_SEATS_FOR_PRO } from '../common/constants';
import { AccountStates } from '../../../../slices/accounts/account';
import { Features } from '../../../../featureToggles/features';
import { FeatureToggle, Off, On } from '../../../../featureToggles';
import { parseNumber } from '../../../../utils/parseNumber';
import { toMonthDayYearFormat } from '../../../accounts/account/utils';

import styles from './index.module.scss';

interface IMapStateProps {
  canAddSeats: boolean;
  currentSeats?: number;
  renewalDate?: Date;
  responsesAllowed?: number;
  usage?: number | null;
  cancellationInProgress?: boolean;
  cadence?: CadenceTypesValues;
  isPaymentMissing: boolean;
  status?: AccountStates;
  // use for Features.PlgExternal2
  responsesAllowedBeforeLapsed?: number;
  seatsToRemoveAtEndOfCycle?: number | BlockedByFeatureType;
  // end of use for Features.PlgExternal2
}

interface ProfessionalSubscriptionState {
  addSeatsModalOpen: boolean;
}

interface IMapDispatchProps {
  push: typeof push;
}

type Props = IMapStateProps & IMapDispatchProps;

class ProfessionalSubscriptionBase extends React.Component<Props, ProfessionalSubscriptionState> {
  constructor(props: Props) {
    super(props);
    this.state = {
      addSeatsModalOpen: false
    };
    this.toggleModalOpen = this.toggleModalOpen.bind(this);
    this.moveToEdit = this.moveToEdit.bind(this);
  }

  private toggleModalOpen() {
    this.setState(state => ({ addSeatsModalOpen: !state.addSeatsModalOpen }));
  }

  private renderModal() {
    const { currentSeats, renewalDate, cadence } = this.props;
    const { addSeatsModalOpen } = this.state;
    if (!addSeatsModalOpen) {
      return null;
    }
    return <AddSeatsModal
      renewalDate={renewalDate}
      onClose={this.toggleModalOpen}
      currentSeats={currentSeats ?? 0}
      cadence={cadence}
    />;
  }

  private renderDescription() {
    const { responsesAllowed = 0, usage = 0, canAddSeats, renewalDate, cancellationInProgress } = this.props;
    if (cancellationInProgress || responsesAllowed > (usage || 0)) {
      return null;
    }
    const resetText = `Responses reset on ${moment(renewalDate).format('MMMM DD, YYYY')}.`;
    if (canAddSeats) {
      return <div className={styles.description}>
        {resetText} <a href='/settings/subscription/compare'>Upgrade your plan</a> to unlock more responses.
      </div>;
    }
    return <div className={styles.description}>
      {resetText}
    </div>;
  }

  private moveToEdit() {
    this.props.push('/settings/subscription/edit');
  }

  private renderCurrentSeats() {
    const { seatsToRemoveAtEndOfCycle = 0, currentSeats = 0 } = this.props;
    const totalSeats = (seatsToRemoveAtEndOfCycle === BlockedByFeature) ? currentSeats : currentSeats + seatsToRemoveAtEndOfCycle;
    return <div className={styles.adminSection}>Current seats: {totalSeats}</div>;
  }

  private hasMarkedSeatsToRemove() {
    const { seatsToRemoveAtEndOfCycle = 0 } = this.props;
    return seatsToRemoveAtEndOfCycle !== BlockedByFeature && seatsToRemoveAtEndOfCycle > 0;
  }

  private renderSeatsToRemove() {
    const { seatsToRemoveAtEndOfCycle, renewalDate } = this.props;
    if (seatsToRemoveAtEndOfCycle === BlockedByFeature || !seatsToRemoveAtEndOfCycle) {
      return null;
    }
    return <div className={styles.removeAlert}>
      {parseNumber(seatsToRemoveAtEndOfCycle)} {seatsToRemoveAtEndOfCycle === 1 ? 'seat' : 'seats'} expiring {toMonthDayYearFormat(renewalDate)}
    </div>;
  }

  private renderAdminOptions() {
    const { canAddSeats, currentSeats = 0, cancellationInProgress, status } = this.props;
    if (!canAddSeats) {
      return null;
    }
    const maxSeatsReached = currentSeats >= MAX_ALLOWED_SEATS_FOR_PRO;
    const tooltipText = this.getTooltipText();
    const shouldShowTooltip = !!tooltipText;
    const isEditButtonDisabled = (status && [AccountStates.LAPSED, AccountStates.DISABLED].includes(status)) || cancellationInProgress;
    const isAddSeatsButtonDisabled = isEditButtonDisabled || this.hasMarkedSeatsToRemove();
    const addSeatsButton = (
      <Button
        type="primary"
        disabled={isAddSeatsButtonDisabled}
        onClick={shouldShowTooltip ? undefined : this.toggleModalOpen}
      >
        Add seats {maxSeatsReached ? <Icon component={LockIcon} /> : null}
      </Button>
    );

    return <>
      {this.renderCurrentSeats()}
      {this.renderSeatsToRemove()}
      {shouldShowTooltip ?
        <SxTooltip
          hideTooltip={!shouldShowTooltip}
          Component={addSeatsButton}
          trigger={['click']}
          tooltipText={tooltipText}
        /> :
        addSeatsButton}
      <FeatureToggle feature={Features.PlgExternal2}>
        <On>
          <Button
            type="link"
            disabled={isEditButtonDisabled}
            className={styles.editButton}
            onClick={this.moveToEdit}
          >
            Edit subscription
          </Button>
        </On>
        <Off />
      </FeatureToggle>
      {this.renderModal()}
    </>;
  }

  private getTooltipText() {
    const { currentSeats = 0, status, isPaymentMissing } = this.props;
    const isStatusOk = status && ![AccountStates.LAPSED, AccountStates.DISABLED].includes(status);
    const maxSeatsReached = currentSeats >= MAX_ALLOWED_SEATS_FOR_PRO;
    if (!isStatusOk || isPaymentMissing) {
      return 'Seats cannot be added. Please check the payment method.';
    }
    if (maxSeatsReached) {
      return `Professional accounts can only add up to ${MAX_ALLOWED_SEATS_FOR_PRO} seats`;
    }
    if (this.hasMarkedSeatsToRemove()) {
      return 'Seats cannot be added while having marked seats to remove.';
    }
    return null;
  }

  render() {
    const { renewalDate, responsesAllowed, responsesAllowedBeforeLapsed, usage, cancellationInProgress, cadence, canAddSeats, status } = this.props;
    return (
      <div className={styles.subscription}>
        <div className={styles.container}>
          <div className={cx(styles.currentPlan, { [styles.ProNotAdmin]: !canAddSeats })}>
            <FeatureToggle feature={Features.PlgExternal2}>
              <On>
                <CurrentPlan
                  accountStatus={status}
                  accountType={AccessTypes.Professional}
                  renewalDate={renewalDate}
                  responsesAllowedBeforeLapsed={responsesAllowedBeforeLapsed}
                  responsesAllowed={responsesAllowed || 0}
                  usage={usage}
                  cancellationInProgress={cancellationInProgress}
                  cadence={cadence}
                />
              </On>
              <Off>
                <CurrentPlan
                  accountStatus={status}
                  accountType={AccessTypes.Professional}
                  renewalDate={renewalDate}
                  responsesAllowed={responsesAllowed || 0}
                  usage={usage}
                  cancellationInProgress={cancellationInProgress}
                  cadence={cadence}
                />
              </Off>
            </FeatureToggle>
            {this.renderDescription()}
            {this.renderAdminOptions()}
          </div>
          <div className={styles.otherPlans}>
            <OtherPlans actualPlan={AccessTypes.Professional} isAdmin={canAddSeats} />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateProps => {
  const user = authSelectors.getLoggedInUser(state);
  const account = accountListSelectors.getAccountById(state, user?.account.id || '');
  const currentSeats = account?.seatsPurchased;
  const renewalDate = (account && account.subscription) ? new Date(account?.subscription?.cycleEndDate * 1000) : undefined;
  const responsesAllowed = account?.responsesAllowed;
  const responsesAllowedBeforeLapsed = account?.responsesAllowedBeforeLapsed;
  const cancellationInProgress = account?.subscription?.cancellationInProgress;
  const cadence = account?.subscription?.cycle;
  const isPaymentMissing = account?.subscription?.isPaymentMissing || false;
  const status = account?.status;
  const seatsToRemoveAtEndOfCycle = account?.subscription?.seatsToRemoveAtEndOfCycle;
  const usage = authSelectors.getAccountUsage(state);
  return {
    canAddSeats: authSelectors.isAuthUserAdminOrOwner(state),
    currentSeats,
    renewalDate,
    responsesAllowed,
    responsesAllowedBeforeLapsed,
    usage,
    cancellationInProgress,
    cadence,
    isPaymentMissing,
    status,
    seatsToRemoveAtEndOfCycle: notAvailablePropExceptIn({ availableIn: [Features.PlgExternal2], expected: seatsToRemoveAtEndOfCycle })
  };
};

export const ProfessionalSubscription = connect(mapStateToProps, {
  push
})(ProfessionalSubscriptionBase);
